import { cn } from '@/lib/cn.js';

type Tone = 'beacon' | 'green' | 'amber' | 'red' | 'slate';

export interface ProgressProps {
  value: number;
  max?: number;
  tone?: Tone;
  className?: string;
  showSegments?: boolean;
  /** Accessible name for screen readers — required for WCAG 2.1 AA
   *  `aria-progressbar-name`. Pass the human-readable metric label
   *  (e.g., "April 2026 usage", "SLA solution"). */
  label?: string;
}

const toneBg: Record<Tone, string> = {
  beacon: 'bg-beacon-400',
  green: 'bg-ok-500',
  amber: 'bg-warn-500',
  red: 'bg-danger-500',
  slate: 'bg-ink-400',
};

export function Progress({
  value,
  max = 100,
  tone = 'beacon',
  className,
  showSegments = false,
  label,
}: ProgressProps) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div
      className={cn(
        'h-1.5 w-full bg-ink-700 rounded-full overflow-hidden relative',
        className
      )}
      role="progressbar"
      aria-label={label ?? 'progress'}
      aria-valuenow={value}
      aria-valuemax={max}
    >
      <div className={cn(toneBg[tone], 'h-full rounded-full')} style={{ width: `${pct}%` }} />
      {showSegments && (
        <div className="absolute inset-0 flex">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 border-r border-ink-800 last:border-r-0"
            />
          ))}
        </div>
      )}
    </div>
  );
}
